import React, { useState } from 'react';
import { Table, Spin, message, Button, Popconfirm, Space, Select } from 'antd';
import { SaveOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { NumberInput } from '../components/number_input';
import { useGetVessels, useUpdateVessel, useDeleteVessel } from '~/services/vessel';
import { TVessel } from '~/types/vessel';

// Google Calendar color options
const VESSEL_COLORS = [
  { value: 'tomato', label: 'Tomato', hex: '#ef4444' },
  { value: 'flamingo', label: 'Flamingo', hex: '#f472b6' },
  { value: 'tangerine', label: 'Tangerine', hex: '#f97316' },
  { value: 'banana', label: 'Banana', hex: '#facc15' },
  { value: 'sage', label: 'Sage', hex: '#4ade80' },
  { value: 'basil', label: 'Basil', hex: '#16a34a' },
  { value: 'peacock', label: 'Peacock', hex: '#3b82f6' },
  { value: 'blueberry', label: 'Blueberry', hex: '#2563eb' },
  { value: 'lavender', label: 'Lavender', hex: '#a78bfa' },
  { value: 'grape', label: 'Grape', hex: '#7c3aed' },
  { value: 'graphite', label: 'Graphite', hex: '#4b5563' },
];

interface VesselsTabProps {
  // Keep the interface for backward compatibility but we'll use real data
  vessels?: any[];
  onVesselsChange?: (vessels: any[]) => void;
}

export const VesselsTab: React.FC<VesselsTabProps> = () => {
  const { data: vesselsData, isLoading, refetch } = useGetVessels();
  const updateVesselMutation = useUpdateVessel();
  const deleteVesselMutation = useDeleteVessel();
  const [loadingVesselId, setLoadingVesselId] = useState<string | null>(null);
  const [editedRates, setEditedRates] = useState<Record<string, number>>({});
  const [editedDirectCosts, setEditedDirectCosts] = useState<Record<string, number>>({});
  const [editedColors, setEditedColors] = useState<Record<string, string>>({});

  const handleRateInputChange = (id: string, value: number) => {
    setEditedRates(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleDirectCostInputChange = (id: string, value: number) => {
    setEditedDirectCosts(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleColorChange = (id: string, value: string) => {
    setEditedColors(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSave = async (vessel: TVessel) => {
    const newRate = editedRates[vessel.id];
    const newDirectCost = editedDirectCosts[vessel.id];
    const newColor = editedColors[vessel.id];

    const hasRateChange = newRate !== undefined && newRate !== vessel.hourlyRate;
    const hasDirectCostChange = newDirectCost !== undefined && newDirectCost !== vessel.directCost;
    const hasColorChange = newColor !== undefined && newColor !== vessel.color;

    if (!hasRateChange && !hasDirectCostChange && !hasColorChange) {
      message.info('No changes to save');
      return;
    }

    setLoadingVesselId(vessel.id);
    try {
      const updatePayload: any = { id: vessel.id };
      if (hasRateChange) updatePayload.hourlyRate = newRate;
      if (hasDirectCostChange) updatePayload.directCost = newDirectCost;
      if (hasColorChange) updatePayload.color = newColor;

      await updateVesselMutation.mutateAsync(updatePayload);
      message.success('Vessel updated successfully');

      setEditedRates(prev => {
        const updated = { ...prev };
        delete updated[vessel.id];
        return updated;
      });
      setEditedDirectCosts(prev => {
        const updated = { ...prev };
        delete updated[vessel.id];
        return updated;
      });
      setEditedColors(prev => {
        const updated = { ...prev };
        delete updated[vessel.id];
        return updated;
      });
      refetch();
    } catch (error) {
      message.error('Failed to update vessel');
      console.error('Error updating vessel:', error);
    } finally {
      setLoadingVesselId(null);
    }
  };

  const handleDelete = async (vessel: TVessel) => {
    try {
      await deleteVesselMutation.mutateAsync(vessel.id);
      message.success(`Vessel "${vessel.name}" deleted successfully`);
      refetch();
    } catch (error) {
      message.error('Failed to delete vessel');
      console.error('Error deleting vessel:', error);
    }
  };

  const getCurrentRate = (vessel: TVessel) => {
    return editedRates[vessel.id] !== undefined ? editedRates[vessel.id] : (vessel.hourlyRate || 0);
  };

  const getCurrentDirectCost = (vessel: TVessel) => {
    return editedDirectCosts[vessel.id] !== undefined ? editedDirectCosts[vessel.id] : (vessel.directCost || 0);
  };

  const getCurrentColor = (vessel: TVessel) => {
    return editedColors[vessel.id] !== undefined ? editedColors[vessel.id] : (vessel.color || '');
  };

  const hasChanges = (vessel: TVessel) => {
    const hasRateChange = editedRates[vessel.id] !== undefined && editedRates[vessel.id] !== vessel.hourlyRate;
    const hasDirectCostChange = editedDirectCosts[vessel.id] !== undefined && editedDirectCosts[vessel.id] !== vessel.directCost;
    const hasColorChange = editedColors[vessel.id] !== undefined && editedColors[vessel.id] !== vessel.color;
    return hasRateChange || hasDirectCostChange || hasColorChange;
  };

  const columns: ColumnsType<TVessel> = [
    {
      title: 'Vessel ID',
      dataIndex: 'vesselRef',
      key: 'vesselRef',
      width: '15%',
    },
    {
      title: 'Vessel Name',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
    },
    {
      title: 'Type',
      dataIndex: 'vesselType',
      key: 'vesselType',
      width: '15%',
      render: (vesselType) => vesselType?.name || 'N/A',
    },
    {
      title: 'Hourly Rate ($)',
      dataIndex: 'hourlyRate',
      key: 'hourlyRate',
      width: '15%',
      render: (_, record: TVessel) => (
        <NumberInput
          value={getCurrentRate(record)}
          prefix="$"
          loading={loadingVesselId === record.id}
          onChange={(newValue) => handleRateInputChange(record.id, newValue)}
        />
      ),
    },
    {
      title: 'Direct Cost ($)',
      dataIndex: 'directCost',
      key: 'directCost',
      width: '12%',
      render: (_, record: TVessel) => (
        <NumberInput
          value={getCurrentDirectCost(record)}
          prefix="$"
          loading={loadingVesselId === record.id}
          onChange={(newValue) => handleDirectCostInputChange(record.id, newValue)}
        />
      ),
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
      width: '15%',
      render: (_, record: TVessel) => {
        const currentColor = getCurrentColor(record);
        const selectedColorObj = VESSEL_COLORS.find(c => c.value === currentColor);

        return (
          <Select
            style={{ width: '100%' }}
            value={currentColor || undefined}
            placeholder="Select color"
            onChange={(value) => handleColorChange(record.id, value)}
            disabled={loadingVesselId === record.id}
            options={VESSEL_COLORS.map(color => ({
              value: color.value,
              label: (
                <div className="flex items-center gap-2">
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      backgroundColor: color.hex,
                      border: '1px solid #d1d5db'
                    }}
                  />
                  <span>{color.label}</span>
                </div>
              )
            }))}
          />
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '13%',
      render: (_, record: TVessel) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<SaveOutlined />}
            loading={loadingVesselId === record.id}
            disabled={!hasChanges(record)}
            onClick={() => handleSave(record)}
          >
            Save
          </Button>
          <Popconfirm
            title="Delete vessel"
            description={`Are you sure you want to delete "${record.name}"?`}
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
            okType="danger"
          >
            <Button
              type="default"
              size="small"
              icon={<DeleteOutlined />}
              loading={deleteVesselMutation.isPending}
              danger
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium mb-2">Vessel Configuration</h3>
          <p className="text-gray-500 mb-4">
            Set hourly rates, direct costs, and calendar colors for each vessel in your fleet
          </p>
        </div>
        <div className="flex justify-center py-8">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Vessel Configuration</h3>
        <p className="text-gray-500 mb-4">
          Set hourly rates, direct costs, and calendar colors for each vessel in your fleet
        </p>
      </div>

      <Table
        columns={columns}
        dataSource={vesselsData?.items || []}
        rowKey="id"
        pagination={false}
        className="bg-white"
        loading={updateVesselMutation.isPending}
      />
    </div>
  );
};
